import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useSearchParams } from "react-router-dom";
import { Chart } from "react-chartjs-2";
import "chart.js/auto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faUserCheck, faRetweet, faSackDollar, faCoins, faStar, faSpinner, faFileCsv, faChevronDown, faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAgencyView } from "../hooks/useAccountType";
import {
  ppapiPost, walletlessGet, apiErrorMessage, rangeParams, defaultRange, fmtUsd, fmtInt, downloadText,
} from "../utils/partnerApi";

/**
 * Campaign Stats (KOL_CAMPAIGNS_AGENCY_PLAN.md §4 + §6).
 * Two sources, merged per campaign code:
 *   - ppapi `campaignStats {from,to}`         → orders / volume / fees, by product
 *   - walletless /partner/campaign-referrals  → referred wallets / active traders
 *   - walletless /partner/points-summary      → referral points (all-time)
 * "Direct" = orders without a campaign (bare link) — campaignCode null.
 */

const ACCENTS = {
  green: { bg: "rgba(58,209,126,.12)", fg: "#3ad17e" },
  blue: { bg: "rgba(90,162,255,.12)", fg: "#5aa2ff" },
  amber: { bg: "rgba(230,192,106,.12)", fg: "#e6c06a" },
  purple: { bg: "rgba(199,155,255,.12)", fg: "#c79bff" },
};

const StatCard = ({ icon, label, value, sub, accent = "green" }) => {
  const a = ACCENTS[accent] || ACCENTS.green;
  return (
    <div className="spKpi">
      <span className="spKpiIco" style={{ background: a.bg, color: a.fg }}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="l">{label}</div>
        <div className="v">{value}</div>
        {sub && <div className="s">{sub}</div>}
      </div>
    </div>
  );
};

export const PRODUCTS = [
  { key: "swap", label: "Swap" },
  { key: "split", label: "Split" },
  { key: "payment", label: "Payment" },
  { key: "spot", label: "Spot" },
  { key: "perps", label: "Perps" },
];

const DIRECT = "__direct__";
const keyOf = (code) => (code ? String(code).toUpperCase() : DIRECT);
const labelOf = (code, campaigns) => {
  if (!code) return "Direct (no campaign)";
  const c = campaigns.find((x) => String(x.code).toUpperCase() === String(code).toUpperCase());
  return c ? c.label : code;
};

const CampaignStats = () => {
  const agencyView = useAgencyView();
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("campaign");

  const [{ from, to }, setRange] = useState(() => defaultRange(30));
  const [campaigns, setCampaigns] = useState([]);
  const [orders, setOrders] = useState(null);      // ppapi campaignStats
  const [referrals, setReferrals] = useState(null); // walletless
  const [points, setPoints] = useState(null);       // walletless
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(focus ? keyOf(focus) : null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const range = rangeParams(from, to);
    Promise.allSettled([
      ppapiPost("listCampaigns", {}, { view: true }),
      ppapiPost("campaignStats", range, { view: true }),
      walletlessGet("/partner/campaign-referrals", range),
      walletlessGet("/partner/points-summary", {}),
    ]).then(([c, o, r, p]) => {
      setCampaigns(c.status === "fulfilled" && Array.isArray(c.value?.campaigns) ? c.value.campaigns : []);
      // Default ppapi payload (action unknown) has no byCampaign → treat as "not available".
      setOrders(o.status === "fulfilled" && Array.isArray(o.value?.byCampaign) ? o.value : null);
      setReferrals(r.status === "fulfilled" && Array.isArray(r.value?.byCampaign) ? r.value : null);
      setPoints(p.status === "fulfilled" && p.value && p.value.referralBonusPoints != null ? p.value : null);
      if (o.status === "rejected" && o.reason?.response?.status === 403) toast.error(apiErrorMessage(o.reason));
    }).finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load, agencyView?.id]);

  // Merge the three sources into one row per campaign key.
  const rows = useMemo(() => {
    const map = new Map();
    const row = (code) => {
      const k = keyOf(code);
      if (!map.has(k)) {
        map.set(k, {
          key: k, code: code || null, label: labelOf(code, campaigns),
          orders: 0, completedOrders: 0, volumeUsd: 0, feesUsd: 0, byProduct: {},
          referredWallets: 0, activeTraders: 0, referredWalletsPoints: 0,
        });
      }
      return map.get(k);
    };
    // every known campaign shows up, even with zeros
    campaigns.forEach((c) => row(c.code));
    row(null);
    (orders?.byCampaign || []).forEach((o) => {
      const r = row(o.campaignCode);
      r.orders += Number(o.orders) || 0;
      r.completedOrders += Number(o.completedOrders) || 0;
      r.volumeUsd += Number(o.volumeUsd) || 0;
      r.feesUsd += Number(o.feesUsd) || 0;
      if (o.byProduct && typeof o.byProduct === "object") r.byProduct = o.byProduct;
    });
    (referrals?.byCampaign || []).forEach((x) => {
      const r = row(x.campaignCode);
      r.referredWallets += Number(x.referredWallets) || 0;
      r.activeTraders += Number(x.activeTraders) || 0;
    });
    (points?.byCampaign || []).forEach((x) => {
      const r = row(x.campaignCode);
      r.referredWalletsPoints += Number(x.referredWalletsPoints) || 0;
    });
    return [...map.values()].sort((a, b) => (b.feesUsd - a.feesUsd) || (b.orders - a.orders) || (a.key === DIRECT ? 1 : -1));
  }, [campaigns, orders, referrals, points]);

  const totals = useMemo(() => rows.reduce((t, r) => ({
    orders: t.orders + r.orders, volumeUsd: t.volumeUsd + r.volumeUsd, feesUsd: t.feesUsd + r.feesUsd,
    referredWallets: t.referredWallets + r.referredWallets, activeTraders: t.activeTraders + r.activeTraders,
  }), { orders: 0, volumeUsd: 0, feesUsd: 0, referredWallets: 0, activeTraders: 0 }), [rows]);

  const chartData = useMemo(() => {
    const shown = rows.filter((r) => r.feesUsd > 0).slice(0, 10);
    return {
      labels: shown.map((r) => r.label),
      datasets: [{ label: "Fees (USD)", data: shown.map((r) => r.feesUsd), backgroundColor: "#3ad17e", borderRadius: 6, maxBarThickness: 44 }],
    };
  }, [rows]);
  const chartOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: "#151617", borderColor: "rgba(255,255,255,.15)", borderWidth: 1, callbacks: { label: (ctx) => fmtUsd(ctx.parsed.y) } } },
    scales: {
      x: { ticks: { color: "#7c847e" }, grid: { color: "rgba(255,255,255,0.04)" } },
      y: { ticks: { color: "#7c847e", callback: (v) => fmtUsd(v) }, grid: { color: "rgba(255,255,255,0.06)" } },
    },
  }), []);

  const exportCsv = () => {
    setExporting(true);
    const header = ["campaign", "code", "orders", "completed", "volume_usd", "fees_usd", "referred_wallets", "active_traders", "referred_points"];
    const lines = rows.map((r) => [
      `"${String(r.label).replace(/"/g, '""')}"`, r.code || "", r.orders, r.completedOrders,
      r.volumeUsd.toFixed(2), r.feesUsd.toFixed(2), r.referredWallets, r.activeTraders, Math.round(r.referredWalletsPoints),
    ].join(","));
    downloadText(`campaign-stats_${from}_${to}.csv`, [header.join(","), ...lines].join("\n"));
    setExporting(false);
  };

  const cols = "24px minmax(180px,1.4fr) 90px 90px 110px 120px 110px";

  return (
    <>
      <Helmet><title>Campaign Stats | Instaswap Partners</title></Helmet>
      <div>
        <div className="secHdr" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h2 className="secTitle">Campaign Stats</h2>
            <p className="secLead">
              Orders, volume and fees per campaign link{agencyView ? ` — viewing ${agencyView.name}` : ""}. Referred users and points come from the
              referral program; “Direct” is the bare link without a campaign code.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label className="fldLbl" style={{ fontSize: 11 }}>From</label>
              <input className="input" type="date" value={from} max={to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} style={{ height: 38, fontSize: 12.5, width: 150 }} />
            </div>
            <div>
              <label className="fldLbl" style={{ fontSize: 11 }}>To</label>
              <input className="input" type="date" value={to} min={from} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} style={{ height: 38, fontSize: 12.5, width: 150 }} />
            </div>
            <button type="button" className="btnGhost sm" onClick={exportCsv} disabled={exporting || loading} style={{ height: 38 }}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </button>
          </div>
        </div>

        <div className="spKpis">
          <StatCard icon={faUsers} label="Referred users" value={fmtInt(totals.referredWallets)} sub="wallets that connected through your links" accent="blue" />
          <StatCard icon={faUserCheck} label="Active traders" value={fmtInt(totals.activeTraders)} sub="referred wallets with a first trade" accent="purple" />
          <StatCard icon={faRetweet} label="Orders" value={fmtInt(totals.orders)} sub="all products, selected range" accent="amber" />
          <StatCard icon={faSackDollar} label="Fees earned" value={fmtUsd(totals.feesUsd)} sub={`on ${fmtUsd(totals.volumeUsd)} volume`} accent="green" />
        </div>

        {points && (
          <div className="spKpis" style={{ marginTop: 12 }}>
            <StatCard icon={faStar} label="Your referral points" value={fmtInt(points.referralBonusPoints)} sub="all-time · 500 per referred trader" accent="green" />
            <StatCard icon={faCoins} label="Points of referred wallets" value={fmtInt(points.referredWalletsPoints)} sub={`all-time · ${fmtInt(points.referredWalletsCount)} wallets`} accent="amber" />
          </div>
        )}

        {loading ? (
          <div className="gtEmpty" style={{ marginTop: 24 }}><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
        ) : (
          <>
            {chartData.labels.length > 0 && (
              <div className="panel" style={{ marginTop: 24 }}>
                <div className="pHd"><div><div className="pTitle">Fees by campaign</div><div className="pSub">{from} → {to}</div></div></div>
                <div style={{ height: 280 }}><Chart type="bar" data={chartData} options={chartOptions} /></div>
              </div>
            )}

            {!orders && (
              <div className="notice" style={{ marginTop: 24 }}>
                <span className="ni">i</span>
                <div>Order statistics are not available yet — referral figures are shown. Fees and volume appear once campaign reporting is enabled on your account.</div>
              </div>
            )}

            <div className="gtScroll" style={{ marginTop: 24 }}>
              <div style={{ minWidth: 820 }}>
                <div className="gtHead" style={{ gridTemplateColumns: cols }}>
                  <span /><span>Campaign</span><span className="r">Referred</span><span className="r">Active</span><span className="r">Orders</span><span className="r">Volume</span><span className="r">Fees</span>
                </div>
                {rows.map((r) => {
                  const open = expanded === r.key;
                  const prod = PRODUCTS.map((p) => ({ ...p, v: r.byProduct?.[p.key] || null })).filter((p) => p.v);
                  return (
                    <React.Fragment key={r.key}>
                      <div className="gtRow" style={{ gridTemplateColumns: cols, cursor: "pointer" }} onClick={() => setExpanded(open ? null : r.key)}>
                        <span style={{ color: "#7c847e" }}><FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} /></span>
                        <span className="cellStrong">
                          {r.label}
                          {r.code && <small style={{ display: "block", color: "#7c847e", fontFamily: "monospace" }}>{r.code}</small>}
                        </span>
                        <span className="r">{fmtInt(r.referredWallets)}</span>
                        <span className="r">{fmtInt(r.activeTraders)}</span>
                        <span className="r">{fmtInt(r.orders)}{r.completedOrders ? <small style={{ color: "#7c847e" }}> · {fmtInt(r.completedOrders)} done</small> : null}</span>
                        <span className="r cellMono">{fmtUsd(r.volumeUsd)}</span>
                        <span className="r cellFee">{fmtUsd(r.feesUsd)}</span>
                      </div>
                      {open && (
                        <div style={{ padding: "10px 14px 16px 44px", borderBottom: "1px solid rgba(255,255,255,.06)", display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
                          {prod.length === 0 ? (
                            <span style={{ color: "#7c847e", fontSize: 12.5 }}>No product breakdown for this range.</span>
                          ) : prod.map((p) => (
                            <div key={p.key} style={{ fontSize: 12.5 }}>
                              <div className="fldLbl" style={{ fontSize: 11, marginBottom: 2 }}>{p.label}</div>
                              <div style={{ color: "#dfe6e0" }}>{fmtInt(p.v.orders)} orders · {fmtUsd(p.v.volumeUsd)} · <b style={{ color: "#3ad17e" }}>{fmtUsd(p.v.feesUsd)}</b></div>
                            </div>
                          ))}
                          {r.referredWalletsPoints > 0 && (
                            <div style={{ fontSize: 12.5 }}>
                              <div className="fldLbl" style={{ fontSize: 11, marginBottom: 2 }}>Referred wallets' points</div>
                              <div style={{ color: "#dfe6e0" }}>{fmtInt(r.referredWalletsPoints)} · all-time</div>
                            </div>
                          )}
                          <Link to={`/transactions?campaign=${encodeURIComponent(r.code || "direct")}`} className="btnGhost sm" style={{ marginLeft: "auto" }}>
                            Orders of this campaign →
                          </Link>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <div className="gtEmpty">No data for this range.</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CampaignStats;
